// Resets of the user's own password / 2FA by an administrator (docs/SECURITY.md): whoever set the
// temporary password could sign in as the user, so every reset and every sign-in after it is shown
// in "Moj račun" (AdminResetList), and the first full sign-in after a reset shows a notice until the
// user confirms it (AdminResetNotice, mounted once by the app shell).
import { useEffect, useState } from 'react';
import type { AdminReset } from '../../../shared/types';
import { getAdminResets, markAdminResetsSeen } from '../lib/account';
import { formatDateTime } from '../lib/format';
import { navigate } from '../lib/router';
import { Modal } from './Dialog';
import { Icon } from './Icon';

export const RESET_ADVICE = 'Če se po ponastavitvi niste prijavili vi, takoj spremenite geslo in obvestite vodstvo.';

/** "geslo", "dvostopenjsko prijavo" or both. */
export function resetWhat(r: Pick<AdminReset, 'password' | 'twoFactor'>): string {
  if (r.password && r.twoFactor) return 'geslo in dvostopenjsko prijavo';
  return r.password ? 'geslo' : 'dvostopenjsko prijavo';
}

/** The resets with the sign-ins after each (time, device, address). */
export function AdminResetList({ resets }: { resets: AdminReset[] }) {
  return (
    <ul className="reset-list">
      {resets.map((r) => (
        <li key={r.id} className="reset-list__item">
          <p>
            <strong>{formatDateTime(r.at)}</strong>: skrbnik <strong>{r.adminName}</strong> je ponastavil(-a) vaše {resetWhat(r)}.
          </p>
          {r.signIns.length === 0 ? (
            <p className="muted small">Po tem se v vaš račun ni prijavil nihče.</p>
          ) : (
            <>
              <p className="muted small">Prijave v vaš račun po tem:</p>
              <ul className="reset-list__signins">
                {r.signIns.map((s) => (
                  <li key={`${s.at}-${s.ip ?? ''}`} className="small">
                    {formatDateTime(s.at)} · {s.deviceName}
                    {s.ip ? ` · IP ${s.ip}` : ''}
                  </li>
                ))}
              </ul>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Shown once per reset after the first full sign-in; the history stays in "Moj račun". */
export function AdminResetNotice() {
  const [resets, setResets] = useState<AdminReset[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getAdminResets().then(
      (history) => {
        if (!alive) return;
        const unseen = new Set(history.unseen);
        const shown = history.resets.filter((r) => unseen.has(r.id));
        setResets(shown.length > 0 ? shown : null);
      },
      // Offline or refused: asked again at the next start.
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, []);

  if (!resets) return null;
  const acknowledge = async (then?: () => void) => {
    setBusy(true);
    try {
      await markAdminResetsSeen(Math.max(...resets.map((r) => r.id)));
    } catch {
      // Shown again next time; the user has read it now.
    }
    setResets(null);
    then?.();
  };

  return (
    <Modal
      title="Skrbnik je ponastavil vaš račun"
      role="alertdialog"
      className="admin-reset-notice"
      actions={
        <>
          <button type="button" className="btn btn--secondary" disabled={busy} onClick={() => void acknowledge(() => navigate({ name: 'settings' }))}>
            <Icon name="lock" size={20} /> Spremeni geslo
          </button>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void acknowledge()} data-autofocus>
            <Icon name="check" size={20} /> Razumem
          </button>
        </>
      }
    >
      <AdminResetList resets={resets} />
      <div className="notice notice--warning">
        <Icon name="alert" size={20} className="notice__icon" />
        <p className="notice__content">
          <strong>{RESET_ADVICE}</strong>
        </p>
      </div>
      <p className="muted small">Ponastavitve in prijave po njih so 90 dni zapisane tudi v Nastavitvah pod »Moj račun«.</p>
    </Modal>
  );
}
